import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

console.log('API_BASE_URL:', API_BASE_URL);

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
    console.log('Making API request:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
apiClient.interceptors.response.use(
  (response) => {
    console.log('API response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API error:', error.message, error.config?.url);
    return Promise.reject(error);
  }
);

export interface CarImport {
  id: number;
  make: string;
  model: string;
  year?: number;
  engine_size?: number;
  fuel_type?: string;
  declared_value?: number;
  calculated_tax?: number;
  applied_tax_percentage?: number;
  registration_number?: string;
  vin?: string;
  decision_date?: string;
  mileage_km?: number;
  drive_type?: string;
  doors?: number;
  body_type?: string;
  transmission?: string;
  individual_characteristics?: string;
}

export interface CarImportSearchParams {
  make?: string;
  model?: string;
  year_from?: number;
  year_to?: number;
  fuel_type?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
}

export const carImportApi = {
  // Get all car imports with optional search parameters
  getAll: async (params: CarImportSearchParams = {}) => {
    const response = await apiClient.get<PaginatedResponse<CarImport>>('/imports/', { params });
    return response.data;
  },
  
  // Get a single car import by ID
  getById: async (id: number) => {
    const response = await apiClient.get<CarImport>(`/imports/${id}`);
    return response.data;
  },
  
  // Create a new car import
  create: async (data: Omit<CarImport, 'id' | 'import_date'>) => {
    const response = await apiClient.post<CarImport>('/imports/', data);
    return response.data;
  },
  
  // Update an existing car import
  update: async (id: number, data: Partial<CarImport>) => {
    const response = await apiClient.put<CarImport>(`/imports/${id}`, data);
    return response.data;
  },
  
  // Delete a car import
  delete: async (id: number) => {
    await apiClient.delete(`/imports/${id}`);
  },
  
  // Upload a file for import
  uploadFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return axios.post(`${API_BASE_URL}/imports/upload/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  getAverageTax: (params: CarImportSearchParams) => {
    return axios.get(`${API_BASE_URL}/imports/average-tax/`, { params });
  },
};
